import { useContext, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import { Box, Container, Link } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/AppContext';
import { SttTopControls } from '../../../sporttia/SttTopControls';
import { SttFileDialog, SttTable } from '../../../sporttia/all';

type Filters = {
    name: string;
};

type Document = {
    id: number;
    name?: string;
};

type DocumentsResponse = {
    rows: Document[];
    count: number;
};

export default function SCDocuments(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const [filters, setFilters] = useState<Filters>({ name: '' });
    const [documents, setDocuments] = useState<DocumentsResponse>();
    const [openFileId, setOpenFileId] = useState<number>();

    /**
     * Load documents.
     * @param p {page, rows} values.
     */
    const loadDocuments = (p = { page: 1, rows: 20 }) => {
        cxt?.api('GET', `/scs/${id}/files`, {
            params: {
                ...filters,
                page: p.page,
                rows: p.rows,
            },
            success: (r: DocumentsResponse) => {
                setDocuments(r);
            },
        });
    };

    useEffect(() => {
        loadDocuments();
    }, []);

    return (
        <Container maxWidth="md">
            <Paper>
                <Box p={2}>
                    <Typography variant="h4" gutterBottom>
                        {t('Documents').toUpperCase()}
                    </Typography>
                    <Typography variant="body2" display="block" gutterBottom>
                        {t('page.scProfile.docs.desc')}
                    </Typography>

                    <SttTopControls
                        p={2}
                        fields={
                            [
                                {
                                    caption: t('Name'),
                                    name: 'name',
                                    type: 'text',
                                    value: filters.name,
                                    onEnterKey: () => loadDocuments(),
                                    xs: 12,
                                    sm: 6,
                                    md: 4,
                                    lg: 3,
                                },
                            ] as any
                        }
                        onChange={({
                            name,
                            value,
                        }: {
                            name: string | undefined;
                            value: unknown;
                        }) => {
                            if (!name) return;
                            setFilters({ ...filters, [name]: value });
                        }}
                        onFilter={loadDocuments}
                    />

                    <SttTable
                        data={documents}
                        columns={[
                            {
                                align: 'left',
                                title: t('Name'),
                                value: (row: Document) => (
                                    <Link
                                        component="button"
                                        underline="hover"
                                        onClick={() => setOpenFileId(row.id)}
                                        sx={{
                                            color: 'var(--brand-text)',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {row.name || t('Noname')}
                                    </Link>
                                ),
                            },
                        ]}
                        onFetch={loadDocuments}
                    />

                    <SttFileDialog
                        idFile={openFileId}
                        onClose={() => setOpenFileId(undefined)}
                        // onUpdate={updateFile}
                    />
                </Box>
            </Paper>
        </Container>
    );
}
